import { t } from "../i18n/t";

/**
 * FX opening-balance spec §3.7: shared between the Dry-run screen's `FxOpeningChecks` and the Scope
 * screen's `FxOpeningBalances`.
 *
 * Round-4 review fix (review-fx-task2-r3.json, item 6): round-3 put these rules ONLY in
 * `FxOpeningChecks.tsx` (confirm-on-BLOCK, per-option labels, the ECB estimate body) -- the Scope
 * screen's own findings list still one-clicked every BLOCK, including the scope-time
 * `fx_opening_balance_sign_mismatch` finding, under a generic "Acknowledge" label, with no ECB body in
 * its `accept_estimate` dialog. Pulled out here rather than left duplicated a second time, so the two
 * screens cannot drift apart on this again.
 */

/** EVERY `FX_OPENING` BLOCK is a real, consequential decision (§3.3's table has no BLOCK whose option is
 * a plain `acknowledge`); a WARN's only option is always `acknowledge`. Confirming on severity alone is
 * therefore not a narrowing of a hand-picked option list, just a correct, future-proof statement of the
 * rule (round-2 MEDIUM-1, opus review round 5). */
export function requiresConfirm(severity: string): boolean {
  return severity === "BLOCK";
}

/** The visible button text must name the actual decision, not a generic "Acknowledge". */
export function labelForOption(
  option: string,
  baseCurrency: string,
  siblings: readonly string[] = [],
): string {
  switch (option) {
    case "use_calculated_figure":
      return t("fx.action_use_calculated");
    case "accept_estimate":
      return t("fx.action_accept_estimate", { base: baseCurrency });
    case "accept_as_entered":
      // Offered next to the calculated figure, "accept as entered" is the operator keeping their own number.
      return siblings.includes("use_calculated_figure")
        ? t("fx.action_keep_my_figure")
        : t("fx.action_accept_as_entered");
    case "accept_foreign_balance_lost":
      return t("fx.action_accept_foreign_balance_lost");
    default:
      return t("fx.action_acknowledge");
  }
}

/**
 * The confirmation dialog's title. `account`, when given, produces the per-account-named title the
 * Scope screen uses (it always has an `FxAccount` to name); omitted, it falls back to the generic title
 * the Dry-run screen uses (only a review item is available there, with no per-account object).
 */
export function confirmTitleFor(option: string, account?: string): string {
  if (account === undefined) {
    switch (option) {
      case "use_calculated_figure":
        return t("fx.finding_confirm_title_calculated_generic");
      case "accept_foreign_balance_lost":
        return t("fx.finding_confirm_title_lost_generic");
      case "accept_as_entered":
        return t("fx.finding_confirm_title_entered_generic");
      default:
        return t("fx.finding_confirm_title_estimate_generic");
    }
  }
  switch (option) {
    case "use_calculated_figure":
      return t("fx.finding_confirm_title_calculated", { account });
    case "accept_foreign_balance_lost":
      return t("fx.finding_confirm_title_lost", { account });
    case "accept_as_entered":
      return t("fx.finding_confirm_title_entered", { account });
    default:
      return t("fx.finding_confirm_title_estimate", { account });
  }
}

/** A rate as an operator reads it: the decimal string as sent, trailing zeros beyond the second dropped. Never
 * parsed to a number -- rates arrive as strings (§A3.5). */
export function plainRate(value: string): string {
  return value.includes(".") ? value.replace(/(\.\d\d\d*?)0+$/, "$1") : value;
}
